/**
 * Receipt splits shared from the Pebla app as self-contained links:
 * https://app.pebla.io/split-receipt?splitReceipt=1&v=2&data=<gzip base64url>&p=<participant>
 *
 * Reads the app's ReceiptSplit JSON. Receipt amounts are whole RWF; shares of
 * items picked by several people keep their decimals (RWF 1,000 / 3 = 333.33).
 */

export type Destination = {
  type: 'creator' | 'merchant';
  name: string;
  identifier: string;
};

export type ReceiptItem = {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
};

export type Charges = {
  tax: number;
  taxIncluded: boolean;
  serviceCharge: number;
  gratuity: number;
  otherCharges: number;
};

export type Receipt = {
  merchantName: string;
  location: string;
  date: string | null;
  items: ReceiptItem[];
  charges: Charges;
};

export type Participant = { id: string; name: string; isCreator: boolean };

export type Share = {
  participantId: string;
  itemsAmount: number;
  tax: number;
  serviceCharge: number;
  gratuity: number;
  otherCharges: number;
};

export type Split = {
  id: string;
  expiresAt: Date | null;
  destination: Destination;
  receipt: Receipt;
  method: 'pickItems' | 'evenly' | 'custom' | string;
  participants: Participant[];
  assignments: Record<string, string[]>;
  shares: Share[];
};

export type Invite = {
  split: Split;
  participantId: string | null;
  paidItemIds: Set<string>;
};

const LINK_VERSION = '2';

type Json = Record<string, any>;

const num = (v: unknown) => (typeof v === 'number' && isFinite(v) ? Math.trunc(v) : 0);
const str = (v: unknown) => (typeof v === 'string' ? v : '');

export function itemTotal(item: ReceiptItem) {
  return item.quantity * item.unitPrice;
}

export function addedTax(c: Charges) {
  return c.taxIncluded ? 0 : c.tax;
}

export function chargeParts(c: Charges) {
  return [addedTax(c), c.serviceCharge, c.gratuity, c.otherCharges];
}

export function subtotal(r: Receipt) {
  return r.items.reduce((sum, i) => sum + itemTotal(i), 0);
}

export function grandTotal(r: Receipt) {
  return subtotal(r) + chargeParts(r.charges).reduce((a, b) => a + b, 0);
}

export function shareTotal(s: Share) {
  return s.itemsAmount + s.tax + s.serviceCharge + s.gratuity + s.otherCharges;
}

export function chargesOf(s: Share) {
  return s.tax + s.serviceCharge + s.gratuity + s.otherCharges;
}

function parseSplit(json: Json): Split | null {
  const r: Json = json.r ?? {};
  const ch: Json = r.ch ?? {};
  const d: Json = json.d ?? {};
  const split: Split = {
    id: str(json.id),
    expiresAt: json.ea ? new Date(json.ea) : null,
    destination: {
      type: d.t === 'merchant' ? 'merchant' : 'creator',
      name: str(d.n),
      identifier: str(d.i),
    },
    receipt: {
      merchantName: str(r.m),
      location: str(r.l),
      date: typeof r.d === 'string' ? r.d : null,
      items: (Array.isArray(r.it) ? r.it : []).map((i: Json) => ({
        id: str(i.id),
        name: str(i.n),
        quantity: num(i.q) || 1,
        unitPrice: num(i.u),
      })),
      charges: {
        tax: num(ch.tx),
        taxIncluded: ch.ti === true,
        serviceCharge: num(ch.sc),
        gratuity: num(ch.gr),
        otherCharges: num(ch.oc),
      },
    },
    method: str(json.me) || 'evenly',
    participants: (Array.isArray(json.ps) ? json.ps : []).map((p: Json) => ({
      id: str(p.id),
      name: str(p.n),
      isCreator: p.c === true,
    })),
    assignments: Object.fromEntries(
      Object.entries((json.as ?? {}) as Json).map(([k, v]) => [
        k,
        Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [],
      ]),
    ),
    shares: (Array.isArray(json.sh) ? json.sh : []).map((s: Json) => ({
      participantId: str(s.pid),
      itemsAmount: num(s.ia),
      tax: num(s.tx),
      serviceCharge: num(s.sc),
      gratuity: num(s.gr),
      otherCharges: num(s.oc),
    })),
  };
  if (!split.id || split.receipt.items.length === 0) return null;
  return split;
}

const unitId = (itemId: string, index: number) => `${itemId}.${index + 1}`;

/** Ids of the single units a link item became (just its own id when it's one unit). */
function unitIds(item: ReceiptItem) {
  return item.quantity > 1
    ? Array.from({ length: item.quantity }, (_, i) => unitId(item.id, i))
    : [item.id];
}

/**
 * Breaks every "3× Coca Cola" into three single Coca Colas so each can be
 * picked, and shared, on its own. Picks and payments made on the whole line
 * carry over to every unit.
 */
export function expandUnits(split: Split, paidItemIds: Set<string>) {
  const items: ReceiptItem[] = [];
  const assignments: Record<string, string[]> = {};
  const paid = new Set<string>();
  for (const item of split.receipt.items) {
    const ids = unitIds(item);
    ids.forEach((id) => {
      items.push(ids.length > 1 ? { id, name: item.name, quantity: 1, unitPrice: item.unitPrice } : item);
      if (split.assignments[item.id]) assignments[id] = [...split.assignments[item.id]];
      if (paidItemIds.has(item.id)) paid.add(id);
    });
  }
  return {
    split: { ...split, receipt: { ...split.receipt, items }, assignments },
    paidItemIds: paid,
  };
}

function base64UrlToBytes(data: string): Uint8Array {
  const b64 = data.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export type Gunzip = (bytes: Uint8Array) => Promise<string>;

/** Browser gunzip (Chrome 80+, Safari 16.4+, Firefox 113+). */
export const browserGunzip: Gunzip = async (bytes) => {
  const stream = new Blob([bytes as BlobPart])
    .stream()
    .pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).text();
};

/** Reads a shared split from the page's query string, or null if invalid. */
export async function readInvite(
  search: string,
  gunzip: Gunzip = browserGunzip,
): Promise<Invite | null> {
  const params = new URLSearchParams(search);
  const data = params.get('data');
  if (params.get('v') !== LINK_VERSION || !data) return null;
  try {
    const json = JSON.parse(await gunzip(base64UrlToBytes(data)));
    if (!json || typeof json !== 'object') return null;
    const split = parseSplit(json);
    if (!split) return null;
    const p = params.get('p');
    const expanded = expandUnits(
      split,
      new Set((Array.isArray(json.pi) ? json.pi : []).filter((x: unknown) => typeof x === 'string')),
    );
    return {
      split: expanded.split,
      participantId: p && split.participants.some((x) => x.id === p) ? p : null,
      paidItemIds: expanded.paidItemIds,
    };
  } catch {
    return null;
  }
}

/** Parts of [amount] in proportion to [weights] that add up exactly to it. */
export function allocate(amount: number, weights: number[]): number[] {
  if (weights.length === 0) return [];
  const positive = weights.map((w) => (w < 0 ? 0 : w));
  let total = positive.reduce((a, b) => a + b, 0);
  const effective = total === 0 ? weights.map(() => 1) : positive;
  if (total === 0) total = weights.length;
  const sign = amount < 0 ? -1 : 1;
  const value = Math.abs(amount);
  const parts = effective.map((w) => Math.floor((value * w) / total));
  let leftover = value - parts.reduce((a, b) => a + b, 0);
  if (leftover > 0) {
    const order = effective
      .map((_, i) => i)
      .sort((a, b) => {
        const ra = (value * effective[a]) % total;
        const rb = (value * effective[b]) % total;
        return ra !== rb ? rb - ra : a - b;
      });
    for (const i of order) {
      if (leftover === 0) break;
      if (effective[i] === 0) continue;
      parts[i] += 1;
      leftover -= 1;
    }
  }
  return parts.map((p) => p * sign);
}

/** Rounded to the cent, for showing and comparing amounts. */
export const round2 = (n: number) => Math.round(n * 100) / 100;

/** Tax and charges follow the items: [base] of the subtotal carries the same part of each. */
function withCharges(receipt: Receipt, participantId: string, base: number): Share {
  const whole = subtotal(receipt);
  const [tax, serviceCharge, gratuity, otherCharges] = chargeParts(receipt.charges).map((part) =>
    whole > 0 ? (part * base) / whole : 0,
  );
  return { participantId, itemsAmount: base, tax, serviceCharge, gratuity, otherCharges };
}

/**
 * What [me] owes when everyone's picks are [picks] (item id to the people on
 * it). An item picked by three people costs each of them a third.
 */
export function liveShare(receipt: Receipt, picks: Record<string, string[]>, me: string): Share {
  let base = 0;
  for (const item of receipt.items) {
    const owners = picks[item.id] ?? [];
    if (owners.includes(me)) base += itemTotal(item) / owners.length;
  }
  return withCharges(receipt, me, base);
}

/** Items nobody has picked yet, with their part of the charges. The host covers these. */
export function unpickedTotal(receipt: Receipt, picks: Record<string, string[]>) {
  const base = receipt.items
    .filter((item) => (picks[item.id] ?? []).length === 0)
    .reduce((sum, item) => sum + itemTotal(item), 0);
  return shareTotal(withCharges(receipt, '', base));
}

/** MoMo only takes whole francs, so a share with cents is paid rounded up. */
export const payAmount = (total: number) => Math.ceil(round2(total));

export const isExpired = (s: Split, now = new Date()) =>
  s.expiresAt !== null && s.expiresAt.getTime() <= now.getTime();

export const canPickItems = (s: Split) => s.method === 'pickItems';

/** People the link can be opened as. The creator is never one of them. */
export function choices(s: Split): Participant[] {
  return s.participants.filter((p) => !p.isCreator);
}

export function firstName(name: string) {
  return name.trim().split(/\s+/)[0] || name;
}

/** "Isaac Mugisha" → "IM", "Anna" → "A". */
export function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0]?.toUpperCase() ?? '')
    .join('');
}

/** The share set by the app for splits that aren't picked item by item. */
export function fixedShare(split: Split, me: string): Share {
  return (
    split.shares.find((s) => s.participantId === me) ?? {
      participantId: me,
      itemsAmount: 0,
      tax: 0,
      serviceCharge: 0,
      gratuity: 0,
      otherCharges: 0,
    }
  );
}

export function sharerName(s: Split) {
  const creator = s.participants.find((p) => p.isCreator);
  if (creator && creator.name.trim()) return creator.name.trim();
  return s.destination.type === 'merchant' ? 'A friend' : s.destination.name || 'A friend';
}

/** `RWF 1,000` or, when there are cents, `RWF 333.33`. */
export function formatRwf(amount: number) {
  const value = round2(Math.abs(amount));
  const sign = amount < 0 && value !== 0 ? '-' : '';
  const digits = Number.isInteger(value) ? 0 : 2;
  return `${sign}RWF ${value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

function localPhone(phone: string) {
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('250') && digits.length === 12) digits = `0${digits.slice(3)}`;
  if (digits.length === 9 && !digits.startsWith('0')) digits = `0${digits}`;
  return digits;
}

/** MTN MoMo dial string, the same one the app uses. */
export function momoDialString(d: Destination, amount: number) {
  const whole = Math.max(0, Math.round(amount));
  if (d.type === 'merchant') {
    return `*182*8*1*${d.identifier.replace(/\D/g, '')}*${whole}#`;
  }
  return `*182*1*1*${localPhone(d.identifier)}*${whole}#`;
}

export function telHref(dial: string) {
  return `tel:${dial.replace(/#/g, '%23')}`;
}

/** Who is paying, entered on the web before picking items. */
export type Payer = { name: string; momo: string };

/** A real name: at least two letters once trimmed. */
export function isValidPayerName(name: string) {
  return (name.match(/\p{L}/gu) ?? []).length >= 2;
}

/**
 * A Rwandan MTN MoMo number as `07XXXXXXXX`, or null. Accepts spaces,
 * dashes, `+250` / `250` and a missing leading zero. Only MTN (078, 079)
 * because the payment code is an MTN MoMo code.
 */
export function normalizeMomoNumber(raw: string): string | null {
  const digits = localPhone(raw.trim());
  return /^07[89]\d{7}$/.test(digits) ? digits : null;
}

/** `0788 123 456` */
export function formatMomoNumber(number: string) {
  return `${number.slice(0, 4)} ${number.slice(4, 7)} ${number.slice(7)}`;
}

const payerKey = (splitId: string) => `pebla.splitReceipt.${splitId}.payer`;

/** Details entered earlier on this browser for this split, if any. */
export function loadPayer(splitId: string, storage: Storage = window.localStorage): Payer | null {
  try {
    const raw = JSON.parse(storage.getItem(payerKey(splitId)) ?? 'null');
    if (!raw || typeof raw.name !== 'string' || typeof raw.momo !== 'string') return null;
    const momo = normalizeMomoNumber(raw.momo);
    return momo && isValidPayerName(raw.name) ? { name: raw.name, momo } : null;
  } catch {
    return null;
  }
}

export function savePayer(splitId: string, payer: Payer, storage: Storage = window.localStorage) {
  try {
    storage.setItem(payerKey(splitId), JSON.stringify(payer));
  } catch {
    /* private mode; the details just aren't remembered */
  }
}

const memberKey = (splitId: string) => `pebla.splitReceipt.${splitId}.member`;

const randomId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

/** This browser's id on a split, so a reload keeps the same picks. */
export function memberIdFor(splitId: string, storage: Storage = window.localStorage) {
  try {
    const known = storage.getItem(memberKey(splitId));
    if (known) return known;
    const id = `w-${randomId()}`;
    storage.setItem(memberKey(splitId), id);
    return id;
  } catch {
    return `w-${randomId()}`;
  }
}
